import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { logSentry } from '@gto/sentry'

import { customInstance } from '@/api/client'
import { toAmount as toSharedAmount } from '@/utils/to-amount'

import { isKnownSettlementProvider } from './labels'

// ─── Domain types ─────────────────────────────────────────────────────────────
// What every consumer (pages, columns) receives: money fields are real `number`s.
// The affiliate BE serialises Decimal money fields as STRINGS on the wire
// (e.g. "12500.75"); the wire types below capture that, and the coercion mappers
// turn them into numbers at this single boundary so no call site has to. Counts /
// IDs are plain integers on the wire and stay numbers.

export type ChannelOverview = {
  payout_method: string
  count: number
  total_amount: number
}

export type EligiblePayout = {
  id: string
  partner_display_name: string
  partner_email: string
  requested_amount: number
  payout_method: string | null
  payout_details: Record<string, string>
  created_at: string
}

export type SettlementPayout = {
  id: string
  partner_display_name: string
  partner_email: string
  requested_amount: number
  payout_method: string | null
  status: string
  provider_transaction_id: string
  created_at: string
}

export type Settlement = {
  id: string
  settlement_number: number
  provider: string
  currency: string
  status: string
  total_amount: number
  payout_count: number
  provider_batch_id: string
  provider_response: Record<string, unknown>
  error_message: string
  created_at: string
  processed_at: string | null
  created_by_email: string | null
  payouts: SettlementPayout[]
}

export type SettlementListItem = Omit<Settlement, 'payouts' | 'provider_response'>

// ─── Wire types ───────────────────────────────────────────────────────────────
// The raw response shape. Money fields arrive as Decimal strings; accept
// `number | string` so a backend that later coerces server-side doesn't break us.

type WireAmount = number | string

export type ChannelOverviewWire = Omit<ChannelOverview, 'total_amount'> & {
  total_amount: WireAmount
}

export type EligiblePayoutWire = Omit<EligiblePayout, 'requested_amount'> & {
  requested_amount: WireAmount
}

export type SettlementPayoutWire = Omit<SettlementPayout, 'requested_amount'> & {
  requested_amount: WireAmount
}

export type SettlementWire = Omit<Settlement, 'total_amount' | 'payouts'> & {
  total_amount: WireAmount
  payouts: SettlementPayoutWire[]
}

export type SettlementListItemWire = Omit<SettlementListItem, 'total_amount'> & {
  total_amount: WireAmount
}

// ─── Boundary coercion ──────────────────────────────────────────────────────
// Single owner of "turn a wire Decimal string into a number" — delegates to the
// shared `@/utils/to-amount` helper so settlements don't carry a parallel NaN
// guard. The settlements-specific log prefix preserves the Sentry breadcrumb
// the existing regression test asserts on.

const SETTLEMENTS_AMOUNT_PREFIX = 'settlements toAmount: non-numeric wire amount'

const toAmount = (value: WireAmount): number => toSharedAmount(value, SETTLEMENTS_AMOUNT_PREFIX)

const mapChannelOverview = (wire: ChannelOverviewWire): ChannelOverview => ({
  ...wire,
  total_amount: toAmount(wire.total_amount),
})

const mapEligiblePayout = (wire: EligiblePayoutWire): EligiblePayout => ({
  ...wire,
  requested_amount: toAmount(wire.requested_amount),
})

const mapSettlementPayout = (wire: SettlementPayoutWire): SettlementPayout => ({
  ...wire,
  requested_amount: toAmount(wire.requested_amount),
})

// Every action the detail page offers is gated on the provider being one the
// registry knows. A provider the FE has never heard of (a new backend enum member
// shipped ahead of the frontend) fails every gate at once, stranding a READY
// settlement with no way to complete it — so it is reported here, at the boundary
// where it arrives, rather than rendering a silent dead end. Read-only: the
// settlement still maps and displays.
//
// Once per provider per session: these mappers run inside a react-query `select`,
// which re-runs on every render of the list page — an unfiltered report would emit
// one event per offending row per keystroke in the filter bar. The condition is a
// standing config mismatch, not an event stream, so the first sighting is the
// whole signal.
const reportedUnknownProviders = new Set<string>()

const reportUnknownProvider = (provider: string): string => {
  if (!isKnownSettlementProvider(provider) && !reportedUnknownProviders.has(provider)) {
    reportedUnknownProviders.add(provider)
    logSentry('settlements: unknown settlement provider — no completion action can be offered:', {
      provider,
    })
  }

  return provider
}

const mapSettlement = (wire: SettlementWire): Settlement => ({
  ...wire,
  provider: reportUnknownProvider(wire.provider),
  total_amount: toAmount(wire.total_amount),
  payouts: wire.payouts.map(mapSettlementPayout),
})

const mapSettlementListItem = (wire: SettlementListItemWire): SettlementListItem => ({
  ...wire,
  provider: reportUnknownProvider(wire.provider),
  total_amount: toAmount(wire.total_amount),
})

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const settlementKeys = {
  all: ['settlements'] as const,
  overview: () => [...settlementKeys.all, 'overview'] as const,
  list: (params?: Record<string, string | undefined>) =>
    [...settlementKeys.all, 'list', params] as const,
  detail: (id: string) => [...settlementKeys.all, 'detail', id] as const,
  eligible: (provider: string) => [...settlementKeys.all, 'eligible', provider] as const,
}

// ─── Queries ────────────────────────────────────────────────────────────────

export const useSettlementOverview = () =>
  useQuery({
    queryKey: settlementKeys.overview(),
    queryFn: () =>
      customInstance<ChannelOverviewWire[]>({
        url: '/v4/affiliates/admin/settlements/overview/',
        method: 'GET',
      }),
    select: (data) => data.map(mapChannelOverview),
  })

export const useSettlementList = (params?: { status?: string; provider?: string }) =>
  useQuery({
    queryKey: settlementKeys.list(params),
    queryFn: () =>
      customInstance<SettlementListItemWire[]>({
        url: '/v4/affiliates/admin/settlements/',
        method: 'GET',
        params,
      }),
    select: (data) => data.map(mapSettlementListItem),
  })

export const useSettlementDetail = (id: string) =>
  useQuery({
    queryKey: settlementKeys.detail(id),
    queryFn: () =>
      customInstance<SettlementWire>({
        url: `/v4/affiliates/admin/settlements/${id}/`,
        method: 'GET',
      }),
    enabled: !!id,
    select: mapSettlement,
  })

export const useEligiblePayouts = (provider: string) =>
  useQuery({
    queryKey: settlementKeys.eligible(provider),
    queryFn: () =>
      customInstance<EligiblePayoutWire[]>({
        url: '/v4/affiliates/admin/settlements/eligible-payouts/',
        method: 'GET',
        params: { provider },
      }),
    enabled: !!provider,
    select: (data) => data.map(mapEligiblePayout),
  })

// ─── Mutations ──────────────────────────────────────────────────────────────

export const useCreateSettlement = () => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (data: { provider: string; currency: string; payout_ids: string[] }) =>
      customInstance<SettlementWire>({
        url: '/v4/affiliates/admin/settlements/',
        method: 'POST',
        data,
      }).then(mapSettlement),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settlementKeys.all })
    },
  })
}

export const useUpdateSettlementPayouts = () => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payout_ids }: { id: string; payout_ids: string[] }) =>
      customInstance<SettlementWire>({
        url: `/v4/affiliates/admin/settlements/${id}/`,
        method: 'PATCH',
        data: { payout_ids },
      }).then(mapSettlement),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: settlementKeys.detail(id) })
      void qc.invalidateQueries({ queryKey: settlementKeys.list() })
    },
  })
}

export const useConfirmSettlement = () => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      customInstance<SettlementWire>({
        url: `/v4/affiliates/admin/settlements/${id}/confirm/`,
        method: 'POST',
      }).then(mapSettlement),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: settlementKeys.detail(id) })
      void qc.invalidateQueries({ queryKey: settlementKeys.list() })
    },
  })
}

export const useProcessSettlement = () => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      customInstance<SettlementWire>({
        url: `/v4/affiliates/admin/settlements/${id}/process/`,
        method: 'POST',
      }).then(mapSettlement),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: settlementKeys.detail(id) })
      void qc.invalidateQueries({ queryKey: settlementKeys.list() })
    },
  })
}

// Completes a READY, non-PayPal (Wise/Confirmo/Manual) settlement after it has been
// disbursed by hand — for Wise/Confirmo via the exported CSV, for Manual directly:
// payouts + commissions → PAID, settlement → COMPLETED. Idempotent; 422s if the
// settlement is not READY, is PayPal, or holds a payout whose method is
// incompatible with the provider. Completing consumes the settlement's payouts
// from the pending pool, so every settlement view — detail, list, overview,
// eligible-payouts — is invalidated.
export const useMarkSettlementPaid = () => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      customInstance<SettlementWire>({
        url: `/v4/affiliates/admin/settlements/${id}/mark-paid/`,
        method: 'POST',
      }).then(mapSettlement),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settlementKeys.all })
    },
  })
}

export const useCancelSettlement = () => {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      customInstance<{ message: string }>({
        url: `/v4/affiliates/admin/settlements/${id}/`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settlementKeys.all })
    },
  })
}

// The export endpoint streams the upload-clean `text/csv` for the file-batch
// providers (WISE / CONFIRMO). It 422s when the provider can't produce a file
// (PAYPAL / MANUAL) or when any payout in the settlement carries invalid payout
// details — the 422 body names the offending partners. Its URL ends in `.csv`
// with NO trailing slash — unlike the sibling endpoints — so keep it verbatim.
// `responseType: 'blob'` keeps axios from coercing the CSV to a string; the auth
// header still comes from the shared instance. The caller saves the Blob via
// `@/utils/download-blob`; on a 422 it reads the partner names off the (blob)
// error body via `extractBlobValidationDetail`.
export const useExportSettlementCsv = () =>
  useMutation({
    mutationFn: ({ settlementId }: { settlementId: string }) =>
      customInstance<Blob>({
        url: `/v4/affiliates/admin/settlements/${settlementId}/export.csv`,
        method: 'GET',
        responseType: 'blob',
      }),
  })
